import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

const ImageUploader = ({ imageUrl, onUpload, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      await onUpload(file);
    } catch (err) {
      alert(err?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileSelect(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  if (imageUrl) {
    return (
      <div className="relative">
        <img
          src={imageUrl}
          alt="Featured"
          className="w-full h-64 object-cover rounded-lg border border-border"
        />
        <button
          onClick={onRemove}
          className="absolute top-8 right-8 p-8 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
        >
          <Icon name="Trash2" size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleClick}
      className={`relative border-2 border-dashed rounded-lg p-32 text-center cursor-pointer transition-colors ${
        dragActive
          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e?.target?.files?.[0])}
        className="hidden"
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Uploading image...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-12">
          <div className="p-16 bg-primary/10 rounded-full">
            <Icon name="Upload" size={32} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-4">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, WEBP up to 5MB
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;